import { useEffect, useState, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import CircularProgress from '@mui/material/CircularProgress';
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';
import ContactEmergencyOutlinedIcon from '@mui/icons-material/ContactEmergencyOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import IosShareOutlinedIcon from '@mui/icons-material/IosShareOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import MyLocationOutlinedIcon from '@mui/icons-material/MyLocationOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { useMutation } from '@tanstack/react-query';
import { Button, Input, PhoneInput } from '@/shared/components/ui';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { userApi } from '@/features/users/api/user.api';
import { sosApi, type SosEventAction } from '@/features/booking/api/sos.api';
import { trackEvent } from '@/shared/lib/analytics';
import { toE164India, isValidIndianMobile } from '@/shared/utils/phone';
import type { Booking } from '@/types';
import {
  buildMapsHref,
  buildSmsHref,
  buildSosShareMessage,
  buildTelHref,
  buildWhatsAppHref,
  canUseNativeShare,
  copyTextToClipboard,
  hasEmergencyContact,
  iceNationalDigits,
  maskPhoneDisplay,
  requestSosLocation,
  shareTextNative,
  type SosGeoLocation,
} from '@/features/booking/utils/sosHelpers';
import { brand } from '@/app/theme/tokens';

interface TravelerSosSheetProps {
  booking: Booking | null;
  open: boolean;
  onClose: () => void;
}

function openExternal(href: string) {
  window.open(href, '_blank', 'noopener,noreferrer');
}

export function TravelerSosSheet({ booking, open, onClose }: TravelerSosSheetProps) {
  const { user, updateUser } = useAuth();
  const [location, setLocation] = useState<SosGeoLocation | null>(null);
  const [locationPending, setLocationPending] = useState(false);
  const [iceExpanded, setIceExpanded] = useState(false);
  const [addingIce, setAddingIce] = useState(false);
  const [iceName, setIceName] = useState('');
  const [icePhone, setIcePhone] = useState('');
  const [iceError, setIceError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [locationCopied, setLocationCopied] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);

  const iceReady = hasEmergencyContact(user);

  useEffect(() => {
    if (!open || !booking) return;

    setIceExpanded(false);
    setAddingIce(!iceReady);
    setIceName(user?.emergencyContactName ?? '');
    setIcePhone(iceNationalDigits(user));
    setIceError(null);
    setFeedback(null);
    setCopied(false);
    setLocationCopied(false);
    setShareBusy(false);
    setLocation(null);

    void logAction('opened');
    trackEvent('sos_opened', { booking_id: booking.id });

    setLocationPending(true);
    void requestSosLocation().then((geo) => {
      setLocation(geo);
      setLocationPending(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only on open
  }, [open, booking?.id]);

  async function logAction(action: SosEventAction, geo?: SosGeoLocation | null) {
    if (!booking) return;
    try {
      await sosApi.logEvent(booking.id, {
        action,
        location: geo ?? location ?? undefined,
      });
    } catch {
      // Logging must never block SOS actions.
    }
  }

  const saveIceMutation = useMutation({
    mutationFn: async () => {
      const name = iceName.trim();
      if (!name) throw new Error('Enter a contact name');
      if (!isValidIndianMobile(icePhone)) {
        throw new Error('Enter a valid 10-digit mobile number');
      }
      return userApi.updateEmergencyContact({
        emergencyContactName: name,
        emergencyContactNumber: toE164India(icePhone),
      });
    },
    onSuccess: (updated) => {
      updateUser(updated);
      setAddingIce(false);
      setIceExpanded(true);
      setIceError(null);
      setFeedback('Emergency contact saved.');
      void logAction('ice_saved');
      trackEvent('sos_ice_saved', { booking_id: booking?.id ?? '' });
    },
    onError: (err: Error) => {
      setIceError(err.message || 'Could not save emergency contact');
    },
  });

  const handleClose = () => {
    void logAction('dismissed');
    trackEvent('sos_dismissed', { booking_id: booking?.id ?? '' });
    onClose();
  };

  const handleEmergencyCall = () => {
    void logAction('emergency_call');
    trackEvent('sos_emergency_call', { booking_id: booking?.id ?? '' });
    window.location.href = 'tel:112';
    setFeedback('Opening dialer for 112…');
  };

  const iceMessage = booking ? buildSosShareMessage(booking, user, location) : '';
  const icePhoneRaw = user?.emergencyContactNumber ?? '';

  const handleIceCall = () => {
    if (!icePhoneRaw) return;
    void logAction('ice_call');
    window.location.href = buildTelHref(icePhoneRaw);
  };

  const handleIceSms = () => {
    if (!icePhoneRaw) return;
    void logAction('ice_sms');
    window.location.href = buildSmsHref(icePhoneRaw, iceMessage);
    setFeedback('Message ready — send it to notify your contact.');
  };

  const handleIceWhatsApp = () => {
    if (!icePhoneRaw) return;
    void logAction('ice_whatsapp');
    openExternal(buildWhatsAppHref(icePhoneRaw, iceMessage));
  };

  const handleGuideCall = () => {
    const phone = booking?.guide?.phone;
    if (!phone) return;
    void logAction('guide_call');
    window.location.href = buildTelHref(phone);
  };

  const handleGuideWhatsApp = () => {
    const wa = booking?.guide?.whatsappNumber || booking?.guide?.phone;
    if (!wa) return;
    void logAction('guide_whatsapp');
    openExternal(buildWhatsAppHref(wa, iceMessage));
  };

  const handleCopyTripDetails = async () => {
    if (!booking) return;
    const message = buildSosShareMessage(booking, user, location);
    const ok = await copyTextToClipboard(message);
    void logAction('copy');
    trackEvent('sos_copy', { booking_id: booking.id });

    if (ok) {
      setCopied(true);
      setLocationCopied(false);
      setFeedback('Trip details copied. Paste them into any chat or SMS.');
      window.setTimeout(() => setCopied(false), 2000);
      return;
    }

    setFeedback('Could not copy automatically. Long-press the text below and copy it.');
  };

  const handleCopyLocation = async () => {
    if (!booking) return;

    let geo = location;
    if (!geo) {
      setLocationPending(true);
      geo = await requestSosLocation();
      setLocation(geo);
      setLocationPending(false);
    }

    if (!geo) {
      setFeedback('Location unavailable. Allow location access and try again.');
      return;
    }

    const mapsUrl = buildMapsHref(geo);
    const ok = await copyTextToClipboard(mapsUrl);
    void logAction('copy_location', geo);
    trackEvent('sos_copy_location', { booking_id: booking.id });

    if (ok) {
      setLocationCopied(true);
      setCopied(false);
      setFeedback('Location link copied.');
      window.setTimeout(() => setLocationCopied(false), 2000);
      return;
    }

    setFeedback('Could not copy location. Try again.');
  };

  const handleShareTripDetails = async () => {
    if (!booking || shareBusy) return;
    const message = buildSosShareMessage(booking, user, location);
    setShareBusy(true);

    try {
      const result = await shareTextNative('Trplin SOS — trip details', message);

      if (result === 'shared') {
        void logAction('share');
        trackEvent('sos_share', { booking_id: booking.id });
        setFeedback('Trip details shared.');
        return;
      }

      if (result === 'cancelled') {
        return;
      }

      // Desktop / unsupported: copy instead so the traveler still gets the text out.
      const ok = await copyTextToClipboard(message);
      void logAction('copy');
      trackEvent('sos_share_fallback_copy', { booking_id: booking.id });
      if (ok) {
        setCopied(true);
        setFeedback(
          canUseNativeShare()
            ? 'Share failed — trip details copied instead.'
            : 'Sharing isn’t available here — trip details copied. Paste into any chat.'
        );
        window.setTimeout(() => setCopied(false), 2000);
      } else {
        setFeedback('Could not share or copy. Try again or screenshot this screen.');
      }
    } finally {
      setShareBusy(false);
    }
  };

  const guide = booking?.guide;
  const hasGuidePhone = Boolean(guide?.phone);
  const hasGuideWa = Boolean(guide?.whatsappNumber || guide?.phone);
  const shareSubtitle = locationPending
    ? 'Getting your location…'
    : location
      ? 'Booking, place, guide + live map pin'
      : 'Booking, place + guide (location unavailable)';

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={handleClose}
      onOpen={() => undefined}
      disableSwipeToOpen
      PaperProps={{
        sx: {
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          maxHeight: '92vh',
          mx: { sm: 'auto' },
          width: { xs: '100%', sm: 440 },
          maxWidth: '100%',
        },
      }}
    >
      <Box sx={{ px: 2.5, pt: 1.5, pb: 3 }}>
        <Box
          sx={{
            width: 40,
            height: 4,
            borderRadius: 2,
            bgcolor: 'divider',
            mx: 'auto',
            mb: 2,
          }}
        />

        <Typography variant="h6" fontWeight={800} sx={{ letterSpacing: '-0.02em' }}>
          Get help now
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, mb: 2, lineHeight: 1.55 }}>
          For danger, call local emergency services first. Trplin is not an emergency response
          service.
        </Typography>

        {feedback && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setFeedback(null)}>
            {feedback}
          </Alert>
        )}

        <Button
          fullWidth
          onClick={handleEmergencyCall}
          startIcon={<PhoneInTalkOutlinedIcon />}
          sx={{
            py: 1.5,
            borderRadius: 2.5,
            fontWeight: 700,
            fontSize: '1rem',
            textTransform: 'none',
            bgcolor: brand.error,
            color: brand.white,
            boxShadow: 'none',
            '&:hover': { bgcolor: '#B91C1C', boxShadow: 'none' },
          }}
        >
          Call 112 — Local emergency
        </Button>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 1, mb: 2.5, textAlign: 'center' }}
        >
          Also available: 100 (police) · 108 (ambulance)
        </Typography>

        <Stack spacing={1.25}>
          {/* ICE row */}
          <SosRow
            icon={<ContactEmergencyOutlinedIcon />}
            title={
              iceReady
                ? `Alert ${user!.emergencyContactName!.trim()}`
                : 'Add emergency contact'
            }
            subtitle={
              iceReady
                ? maskPhoneDisplay(user?.emergencyContactNumber)
                : 'Someone you trust to call or message'
            }
            onClick={() => {
              if (iceReady) setIceExpanded((v) => !v);
              else setAddingIce(true);
            }}
          />

          {iceReady && iceExpanded && (
            <Stack spacing={1} sx={{ px: 0.5, pb: 0.5 }}>
              <Stack direction="row" spacing={1}>
                <Chip
                  icon={<CallOutlinedIcon />}
                  label="Call"
                  clickable
                  onClick={handleIceCall}
                  sx={chipSx}
                />
                <Chip
                  icon={<SmsOutlinedIcon />}
                  label="SMS"
                  clickable
                  onClick={handleIceSms}
                  sx={chipSx}
                />
                <Chip
                  icon={<WhatsAppIcon />}
                  label="WhatsApp"
                  clickable
                  onClick={handleIceWhatsApp}
                  sx={chipSx}
                />
              </Stack>
              <Button
                intent="ghost"
                size="small"
                onClick={() => {
                  setAddingIce(true);
                  setIceName(user?.emergencyContactName ?? '');
                  setIcePhone(iceNationalDigits(user));
                  setIceError(null);
                }}
                sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
              >
                Change contact
              </Button>
            </Stack>
          )}

          {addingIce && (
            <Box
              sx={{
                p: 1.75,
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.default',
              }}
            >
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.25 }}>
                Emergency contact
              </Typography>
              {iceError && (
                <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2 }}>
                  {iceError}
                </Alert>
              )}
              <Stack spacing={1.5}>
                <Input
                  label="Name"
                  value={iceName}
                  onChange={(e) => setIceName(e.target.value)}
                  fullWidth
                  size="small"
                />
                <PhoneInput
                  label="Phone"
                  value={icePhone}
                  onChange={setIcePhone}
                  fullWidth
                  size="small"
                />
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  {iceReady && (
                    <Button
                      intent="ghost"
                      size="small"
                      onClick={() => {
                        setAddingIce(false);
                        setIceError(null);
                      }}
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    intent="primary-dark"
                    size="small"
                    disabled={saveIceMutation.isPending}
                    onClick={() => saveIceMutation.mutate()}
                  >
                    {saveIceMutation.isPending ? 'Saving…' : 'Save'}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          )}

          {/* Guide row */}
          <SosRow
            icon={<PersonOutlineIcon />}
            title={guide?.fullName ? `Contact ${guide.fullName}` : 'Contact your guide'}
            subtitle={
              hasGuidePhone || hasGuideWa
                ? 'Call or WhatsApp your guide'
                : 'Guide contact not available yet'
            }
            disabled={!hasGuidePhone && !hasGuideWa}
            onClick={() => {
              if (hasGuidePhone) handleGuideCall();
              else if (hasGuideWa) handleGuideWhatsApp();
            }}
            trailing={
              (hasGuidePhone || hasGuideWa) && (
                <Stack direction="row" spacing={0.75}>
                  {hasGuidePhone && (
                    <Chip
                      icon={<CallOutlinedIcon />}
                      label="Call"
                      size="small"
                      clickable
                      onClick={(e) => {
                        e.stopPropagation();
                        handleGuideCall();
                      }}
                      sx={chipSx}
                    />
                  )}
                  {hasGuideWa && (
                    <Chip
                      icon={<WhatsAppIcon />}
                      label="WhatsApp"
                      size="small"
                      clickable
                      onClick={(e) => {
                        e.stopPropagation();
                        handleGuideWhatsApp();
                      }}
                      sx={chipSx}
                    />
                  )}
                </Stack>
              )
            }
          />

          {/* Share row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              width: '100%',
              p: 1.5,
              borderRadius: 2.5,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: brand.white,
            }}
          >
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                bgcolor: brand.errorLight,
                color: brand.error,
                '& .MuiSvgIcon-root': { fontSize: 22 },
              }}
            >
              <IosShareOutlinedIcon />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" fontWeight={700} noWrap>
                Share trip details
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                {shareSubtitle}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Tooltip title={copied ? 'Copied' : 'Copy full trip details'}>
                <IconButton
                  size="small"
                  aria-label="Copy trip details"
                  onClick={() => void handleCopyTripDetails()}
                  sx={shareIconBtnSx}
                >
                  {copied ? (
                    <CheckRoundedIcon sx={{ fontSize: 18, color: brand.success }} />
                  ) : (
                    <ContentCopyOutlinedIcon sx={{ fontSize: 18 }} />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip
                title={
                  locationPending
                    ? 'Getting location…'
                    : locationCopied
                      ? 'Location copied'
                      : location
                        ? 'Copy location link only'
                        : 'Copy location (will request access if needed)'
                }
              >
                <span>
                  <IconButton
                    size="small"
                    aria-label="Copy location only"
                    disabled={locationPending}
                    onClick={() => void handleCopyLocation()}
                    sx={shareIconBtnSx}
                  >
                    {locationPending ? (
                      <CircularProgress size={16} thickness={5} />
                    ) : locationCopied ? (
                      <CheckRoundedIcon sx={{ fontSize: 18, color: brand.success }} />
                    ) : (
                      <MyLocationOutlinedIcon sx={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  canUseNativeShare()
                    ? 'Share via apps'
                    : 'Share isn’t available here — copies instead'
                }
              >
                <span>
                  <IconButton
                    size="small"
                    aria-label="Share trip details"
                    disabled={shareBusy}
                    onClick={() => void handleShareTripDetails()}
                    sx={shareIconBtnSx}
                  >
                    {shareBusy ? (
                      <CircularProgress size={16} thickness={5} />
                    ) : (
                      <IosShareOutlinedIcon sx={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Box>
        </Stack>

        <Divider sx={{ my: 2.5 }} />

        <Button fullWidth intent="ghost" onClick={handleClose} sx={{ textTransform: 'none' }}>
          I&apos;m OK — close
        </Button>
      </Box>
    </SwipeableDrawer>
  );
}

function SosRow({
  icon,
  title,
  subtitle,
  onClick,
  disabled,
  trailing,
}: {
  icon: ReactNode;
  title: string;
  subtitle: string;
  onClick?: () => void;
  disabled?: boolean;
  trailing?: ReactNode;
}) {
  return (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        width: '100%',
        textAlign: 'left',
        p: 1.5,
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: brand.white,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        transition: 'border-color 0.15s ease, background-color 0.15s ease',
        '&:hover:not(:disabled)': {
          borderColor: 'error.light',
          bgcolor: brand.errorLight,
        },
      }}
    >
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          bgcolor: brand.errorLight,
          color: brand.error,
          '& .MuiSvgIcon-root': { fontSize: 22 },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" fontWeight={700} noWrap>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
          {subtitle}
        </Typography>
      </Box>
      {trailing}
    </Box>
  );
}

const chipSx = {
  height: 32,
  fontWeight: 600,
  bgcolor: brand.errorLight,
  color: brand.error,
  '& .MuiChip-icon': { color: brand.error },
} as const;

const shareIconBtnSx = {
  width: 36,
  height: 36,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2,
  color: brand.error,
  bgcolor: brand.white,
  '&:hover': {
    bgcolor: brand.errorLight,
    borderColor: brand.error,
  },
  '&.Mui-disabled': {
    opacity: 0.55,
  },
} as const;
